import { auth } from './firebase';

const ORG_PATH_RE = /^\/org\/(\d+)(\/|$)/;

interface ApiOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
}

/** Parse ``/org/{orgId}/…`` from the current browser URL. */
export function resolveActiveOrganizationId(): string | null {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(ORG_PATH_RE);
  return match ? match[1] : null;
}

/** Path prefix ``/organizations/{orgId}/…`` from the URL, or throws. */
export function orgApiPath(suffix: string): string {
  const id = resolveActiveOrganizationId();
  if (!id) throw new Error('No organization in URL');
  const p = suffix.startsWith('/') ? suffix : `/${suffix}`;
  return `/organizations/${id}${p}`;
}

/** Same-origin absolute API URL for cookie-authenticated media ``src`` / ``download``. */
export function orgMediaUrl(suffix: string): string {
  return `/api/v1${orgApiPath(suffix)}`;
}

/** Build a UI path under ``/org/{orgId}``. */
export function orgUiPath(orgId: string | number, suffix: string): string {
  const p = suffix.startsWith('/') ? suffix : `/${suffix}`;
  if (p === '/') return `/org/${orgId}`;
  return `/org/${orgId}${p}`;
}

export function formatApiErrorDetail(detail: unknown): string {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg: unknown }).msg);
        }
        return JSON.stringify(item);
      })
      .filter(Boolean)
      .join('; ');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

/** Extract a human-readable message from a failed API ``fetch`` response. */
export async function readApiErrorMessage(res: Response): Promise<string> {
  const contentType = res.headers.get('content-type') ?? '';
  const text = await res.text();
  if (contentType.includes('application/json') && text) {
    try {
      const body = JSON.parse(text) as { detail?: unknown };
      const message = formatApiErrorDetail(body.detail);
      if (message) return message;
    } catch {
      // fall through
    }
  }
  return text.trim() || `API error ${res.status}: ${res.statusText}`;
}

/**
 * Authenticated fetch wrapper that attaches the Firebase ID token.
 * Org scope must appear in ``path`` (e.g. ``/organizations/{id}/campaigns``).
 */
export async function apiClient<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...options.headers,
  };
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`/api/v1${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await res.json()) as T;
  }

  return (await res.text()) as T;
}

/** Trigger a browser download for a cookie-authenticated org media URL. */
export function downloadOrgMediaFile(apiSuffix: string, filename: string): void {
  const a = document.createElement('a');
  a.href = orgMediaUrl(apiSuffix);
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
